import { useRef } from "react";
import { Popover, Button, Spin, Cascader, Modal, Tooltip, DatePicker } from "antd";
import moment from "moment-timezone";
import { find, get } from "lodash";
import type { DefaultOptionType } from 'antd/es/cascader';

import Icon from "@assets/icon_config";
import { CanEdit } from "@components/accessLevel";
import HealthemSpinner, { Spinner } from "@components/healthemSpinner/healthemSpinner";
import { NOT_ASSIGNED, dateFormatConfig } from "@utils/constants";
import { ACTIVE, COMPLETE, NEXT, PREVIOUS, SYSTEM } from "../../constants";
import { caseLessComparision, getClientName, milleniumClientCheck } from "@utils/helpers";
import "./intervention.scss";
import CustomTooltip from "@pages/tool-tip";
import { CarePlanPdf } from "../care-plan-report/care-plan-report";
import { useCarePlanReport } from "../../customHooks";

function targetDateContent({ interventionStatus, isTargetDateLoading, interventionEndDate, dateFormat, onChangeTargetDate, interventionObject, dischargeDate }: any) {
  
  return (
    <div className={`editable-date-handler editable-date-handler__item`}>
      <span className="editable-date-handler__item__calender-icon">
        <Icon name="calender" />
      </span>
      <div className="editable-date-handler__item__label">Target Date</div>
      <div className={`editable-date-handler__item__value dateValue ${interventionStatus !== ACTIVE || isTargetDateLoading ? "disableDate" : "notDisabled"}`}>
        <CanEdit hide={false}>
          <DatePicker
            value={moment.utc(interventionEndDate).local().format("YYYY-MM-DD") === "9999-12-31" ? undefined : moment.utc(interventionEndDate).local()}
            format={dateFormat}
            allowClear={false}
            onChange={(date) => onChangeTargetDate(date, interventionObject)}
            disabled={interventionStatus !== ACTIVE || isTargetDateLoading}
            disabledDate={(current) => {
              return current && current < moment(dischargeDate);
            }}
            placeholder={"--/--/--"}
          />
        </CanEdit>
      </div>
      {isTargetDateLoading ? (
        <div className="editable-date-handler__item__preLoader">
          <Spin size="small" indicator={<HealthemSpinner />} />
        </div>
      ) : null}
    </div>
  );
}

const filterValue = (inputValue: string, path: DefaultOptionType[]) =>
path.some(
  (option) => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
);

function userAssignmnetContent({
  openAssigneeList,
  tempAssignee,
  userAssignmentVal,
  AssigneeDetails,
  handleAssigneeList,
  assigneeHasAccess,
  assigneeNoAccess,
  handleCancelAssignment,
  handleReassignment,
  requiredUser,
}: any) {
  return (
    <>
      {openAssigneeList ? (
        <Cascader
          className="ui-select multilevelSelect"
          placeholder="Please select"
          dropdownClassName="ui-program-interventions__intervention__detail__assign__dropdown"
          // showSearch={{ filterValue }}
          displayRender={(labels: string[], selectedOptions) => {
            return `${labels[1]}`;
          }}
          value={tempAssignee || userAssignmentVal}
          allowClear={false}
          options={AssigneeDetails}
          onChange={handleAssigneeList}
        />
      ) : (
        <></>
      )}
      <Modal
        open={assigneeHasAccess || assigneeNoAccess}
        title={"Care Program Re-assignment"}
        wrapClassName="custom-action-modal"
        className="reassignment-modal"
        footer={false}
        onCancel={() => handleCancelAssignment()}
        centered={true}
      >
        <div>
          <div className="reassignment-modal_content">Are you sure you want to re-assign the Care Program to a new Care Team member ?</div>
          <div className="reassignment-modal_content-sub">{assigneeHasAccess ? "" : "(Please note that the new Care Team member does not have access to the patient.)"}</div>
        </div>
        <p>
          <div className="reassignment-modal_btn">
            <Button size="large" className="f16 w500 close-button" onClick={() => handleCancelAssignment()}>
              {"Cancel"}
            </Button>
            <Button type="primary" size="large" className="f16 w500 confirm-button" onClick={() => handleReassignment(requiredUser)}>
              OK
            </Button>
          </div>
        </p>
      </Modal>
    </>
  );
}

function StartDate({ assignedPrograms, setProgramLoading, initialLoad, programId, patientProgramId, dateFormat, dischargeDate, status }: any) {
  const changeInstance = (type: any, dischargeDate: any) => {
    setProgramLoading(true);
    const _patientProgramId = find(assignedPrograms, { id: programId })?.patientProgramId;
    let startDateUnix = `${status === COMPLETE ? 0 : 1}${moment(dischargeDate).unix()}`
    initialLoad({ programId: programId, patientProgramId: type === null ? _patientProgramId : patientProgramId, startDate: dischargeDate, instanceNeeded: type, startDateUnix });
  };

  return (
    <div className="editable-date-handler__item extra">
      <span className="editable-date-handler__item__calender-icon">
        <Icon name="calender" />
      </span>
      <div className="editable-date-handler__item__label">Start Date</div>
      <div className="editable-date-handler__item__value dateValue editable">
        <span
          className="arrow arrowLeft"
          onClick={() => {
            changeInstance(PREVIOUS, dischargeDate);
          }}
        >
          <Icon name="arrowLeft" />
        </span>
        <span className="date">{dischargeDate?.format(dateFormat)}</span>
        <span
          className="arrow arrowRight"
          onClick={() => {
            changeInstance(NEXT, dischargeDate);
          }}
        >
          <Icon name="arrowRight" />
        </span>
        <div
          className="arrow arrowDoubleRight"
          onClick={() => {
            changeInstance(null, null);
          }}
        >
          <Icon name="doubleArrowRight" />
        </div>
      </div>
    </div>
  );
}

function TargetDate(props: any) {
  return (
    <Popover className="editable-date-handler__icon" placement="bottomLeft" content={() => targetDateContent(props)}>
      <span>
        <Icon name="targetCalender" />
      </span>
    </Popover>
  );
}

function UserAssignment({
  careManagerAssigned,
  programsData,
  openAssigneeList,
  interventionStatus,
  handleOpenChange,
  setOpenAssigneeList,
  tempAssignee,
  userAssignmentVal,
  AssigneeDetails,
  handleAssigneeList,
  assigneeHasAccess,
  assigneeNoAccess,
  handleCancelAssignment,
  handleReassignment,
  requiredUser,
}: any) {
  return (
    <Tooltip title={careManagerAssigned ? careManagerAssigned : NOT_ASSIGNED}>
      <Popover
        placement="bottom"
        content={() =>
          userAssignmnetContent({
            openAssigneeList,
            tempAssignee,
            userAssignmentVal,
            AssigneeDetails,
            handleAssigneeList,
            assigneeHasAccess,
            assigneeNoAccess,
            handleCancelAssignment,
            handleReassignment,
            requiredUser,
          })
        }
        trigger="click"
        open={openAssigneeList && interventionStatus !== COMPLETE}
        onOpenChange={handleOpenChange}
      >
        <div className={`ui-program-interventions__intervention__detail__assign__status--icon ${careManagerAssigned ? "" : ""}`} onClick={() => setOpenAssigneeList(true)}>
          <Icon name={careManagerAssigned ? "user-filled" : "user-unfilled"} />
        </div>
      </Popover>
    </Tooltip>
  );
}

export function CompleteButton({ completionReason, interventionStatus, isAutoClosure, completedDaysPercent, updateIntervention, interventionId, activeTasks, activeGoals, remainingDays, isAutoClosed , isLoading} : any) {
  return (
    <CanEdit>
      <CustomTooltip toolTipvalue={completionReason}>
        <Spinner loading={isLoading}>
          <span className="ui-program-interventions__intervention__detail__right-options__complete-button">
            <Button
              type="primary"
              size="large"
              disabled={interventionStatus !== ACTIVE}
              className="complete-button"
              style={
                {
                  "--color-percent": isAutoClosure ? completedDaysPercent : "0%",
                  "--color_code": interventionStatus === COMPLETE ? "#dfe1e6" : "#EF6D2A",
                  "--padding-bottom": isAutoClosure && interventionStatus === ACTIVE ? "10px" : "0px",
                } as React.CSSProperties
              }
              onClick={() => {
                updateIntervention({
                  id: interventionId,
                  status: COMPLETE,
                  activeItems: activeTasks.length > 0 || activeGoals.length > 0,
                });
              }}
            >
              {interventionStatus === ACTIVE ? (
                isAutoClosure ? (
                  <span className="complete-button_auto-closure">
                    <span>Complete Now</span>
                    <sub className="complete-button_auto-closure_sub">{remainingDays} Days Left</sub>
                  </span>
                ) : (
                  <>Complete</>
                )
              ) : interventionStatus === COMPLETE ? (
                isAutoClosed === "SYSTEM" ? (
                  <>Auto-Closed</>
                ) : (
                  <>Completed</>
                )
              ) : (
                <>Complete</>
              )}
            </Button>
          </span>
        </Spinner>
      </CustomTooltip>
    </CanEdit>
  );
}

export function SubHeader({
  assignedPrograms,
  setProgramLoading,
  initialLoad,
  programId,
  patientProgramId,
  dischargeDate,
  interventionStatus,
  isTargetDateLoading,
  interventionEndDate,
  onChangeTargetDate,
  interventionObject,
  programsData,
  openAssigneeList,
  handleOpenChange,
  setOpenAssigneeList,
  tempAssignee,
  userAssignmentVal,
  AssigneeDetails,
  handleAssigneeList,
  assigneeHasAccess,
  assigneeNoAccess,
  handleCancelAssignment,
  handleReassignment,
  requiredUser,
  isAutoClosed
}: any) {
  const dateFormat = dateFormatConfig.get(getClientName())["defaultDateFormat"];
  const assignedUserId = get(programsData, 'assgnd_user_id');
  const careManagerAssigned = !assignedUserId && caseLessComparision(isAutoClosed , SYSTEM) && get(programsData, 'status') === COMPLETE ? SYSTEM : (assignedUserId ? get(programsData, "userName") : '') ;
  return (
    <div className="editable-date-handler">
      <StartDate
        assignedPrograms={assignedPrograms}
        setProgramLoading={setProgramLoading}
        initialLoad={initialLoad}
        programId={programId}
        patientProgramId={patientProgramId}
        dateFormat={dateFormat}
        dischargeDate={dischargeDate}
        status={get(programsData, 'status')}
      />
      <TargetDate
        interventionStatus={interventionStatus}
        isTargetDateLoading={isTargetDateLoading}
        interventionEndDate={interventionEndDate}
        dateFormat={dateFormat}
        onChangeTargetDate={onChangeTargetDate}
        interventionObject={interventionObject}
        dischargeDate={dischargeDate}
      />
      {milleniumClientCheck() ? (
        <UserAssignment
          careManagerAssigned={careManagerAssigned}
          programsData={programsData}
          openAssigneeList={openAssigneeList}
          interventionStatus={interventionStatus}
          handleOpenChange={handleOpenChange}
          setOpenAssigneeList={setOpenAssigneeList}
          tempAssignee={tempAssignee}
          userAssignmentVal={userAssignmentVal}
          AssigneeDetails={AssigneeDetails}
          handleAssigneeList={handleAssigneeList}
          assigneeHasAccess={assigneeHasAccess}
          assigneeNoAccess={assigneeNoAccess}
          handleCancelAssignment={handleCancelAssignment}
          handleReassignment={handleReassignment}
          requiredUser={requiredUser}
        />
      ) : null}
    </div>
  );
}

export function ReportToUpload({ payload , referralData , carePlanReportData , carePlanProgramData , rules}: any) {
  const ref: any = useRef(null);

  return (
    <div className='ui-program-interventions__auto-upload-preview'>
      <CarePlanPdf zoom={1} ref={ref} payload={payload} carePlanReportData={carePlanReportData} referralData={referralData} 
      carePlanProgramData={carePlanProgramData} rules={rules} />
    </div>
  );
}